<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAccessStore } from '@/stores/access'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, Pencil, RotateCcw } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import RoleFormDialog from './RoleFormDialog.vue'
import { can } from '@/composables/useCan'
import { apiErrorDetail } from '@/utils/roles'
import {
  DATA_SCOPES,
  LEVELS,
  SCOPE_LABELS,
  applyEdits,
  applyLevel,
  applySees,
  defaultLabel,
  editsOf,
  levelLabel,
  levelOf,
  levelsFor,
  normalizePermissions,
  samePermissions,
  sectionsFor,
  seesOf,
  type AccessDomain,
  type DataScope,
  type DomainSpec,
  type Level,
  type Permissions,
  type Role,
} from '@/utils/accessDomains'

const props = defineProps<{ roleId: number }>()

const accessStore = useAccessStore()
const route = useRoute()
const router = useRouter()

const role = computed<Role | undefined>(() => accessStore.role(props.roleId))
const specs = computed<DomainSpec[]>(() => (role.value ? accessStore.domains(role.value.kind) : []))
const sections = computed(() => (role.value ? sectionsFor(role.value.kind, specs.value) : []))
const seeded = computed(() => !!role.value?.template_key)
const readonly = computed(() => !can('org_role', 'update'))
const hasScopes = computed(() => specs.value.some(d => d.scoped))

const listRoute = computed(() => ({
  name: 'OrgRoles',
  params: { org_id: route.params.org_id },
  query: { scope: role.value?.kind ?? 'org' },
}))

const saved = computed(() => normalizePermissions(specs.value, role.value?.permissions ?? {}))
const template = computed(() => normalizePermissions(specs.value, role.value?.template_permissions ?? {}))

const draft = ref<Permissions>({})
const resetDraft = () => {
  draft.value = { ...saved.value }
}
watch(() => `${role.value?.role_id}:${specs.value.length}`, resetDraft, { immediate: true })
watch(saved, (next, prev) => {
  draft.value = Object.fromEntries(
    specs.value.map(d => [d.key, samePermissions(draft.value[d.key], prev[d.key]) ? next[d.key] : draft.value[d.key]]),
  )
})

const isDirty = computed(() => specs.value.some(d => !samePermissions(draft.value[d.key], saved.value[d.key])))

const isCustom = (domain: AccessDomain) => seeded.value && !!role.value?.customized_domains.includes(domain.key)
const differsFromDefault = (domain: AccessDomain) =>
  seeded.value && !samePermissions(draft.value[domain.key], template.value[domain.key])

const current = (domain: DomainSpec) => draft.value[domain.key] ?? {}
const setDomain = (domain: DomainSpec, value: Permissions[string]) => {
  draft.value = { ...draft.value, [domain.key]: value }
}
const setLevel = (domain: DomainSpec, level: Level) => setDomain(domain, applyLevel(domain, current(domain), level))
const setAll = (level: Level) => specs.value.forEach(d => setLevel(d, level))

const showsSees = (domain: AccessDomain) => domain.scoped && !!current(domain).read
const showsEdits = (domain: AccessDomain) => domain.scoped && !!(current(domain).update || current(domain).delete)
const scopeValue = (domain: AccessDomain, kind: 'sees' | 'edits') =>
  kind === 'sees' ? seesOf(current(domain)) : editsOf(current(domain))
const setScope = (domain: AccessDomain, kind: 'sees' | 'edits', value: DataScope) =>
  setDomain(domain, kind === 'sees' ? applySees(current(domain), value) : applyEdits(current(domain), value))
const rank = (scope: DataScope) => DATA_SCOPES.indexOf(scope)

const saving = ref(false)

const save = async () => {
  if (!role.value) return
  saving.value = true
  try {
    await accessStore.updateRole({
      roleId: role.value.role_id,
      permissions: Object.fromEntries(
        specs.value
          .filter(d => !samePermissions(draft.value[d.key], saved.value[d.key]))
          .map(d => [d.key, normalizePermissions([d], draft.value)[d.key]]),
      ),
    })
    resetDraft()
    toast({ title: `${role.value.name} saved` })
  } catch (e) {
    toast({ title: apiErrorDetail(e, "Couldn't save this role. Reload and try again."), variant: 'destructive' })
  } finally {
    saving.value = false
  }
}

const resetToDefault = async (domain?: AccessDomain) => {
  if (!role.value) return
  try {
    await accessStore.resetRole({ roleId: role.value.role_id, domain: domain?.key })
    if (domain) setDomain(domain, saved.value[domain.key])
    else resetDraft()
    toast({ title: domain ? `${domain.label} reset to default` : `${role.value.name} reset to default` })
  } catch (e) {
    toast({ title: apiErrorDetail(e, "Couldn't reset this role. Reload and try again."), variant: 'destructive' })
  }
}

const discardDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const resetDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)

const goBack = () => {
  if (isDirty.value) {
    discardDialog.value?.open()
    return
  }
  router.push(listRoute.value)
}

const renameOpen = ref(false)
</script>

<template>
  <div>
    <button
      type="button"
      class="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      @click="goBack"
    >
      <ArrowLeft class="h-4 w-4" />
      Roles
    </button>

    <div v-if="!role" class="py-16 text-center text-sm text-muted-foreground">
      Role not found
    </div>

    <template v-else>
      <div class="mb-3 flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-semibold leading-tight">{{ role.name }}</h3>
            <Button
              v-if="!readonly"
              variant="ghost"
              size="sm"
              class="h-6 w-6 p-0 text-muted-foreground"
              title="Rename"
              @click="renameOpen = true"
            >
              <Pencil class="h-3.5 w-3.5" />
            </Button>
          </div>
          <p v-if="role.description" class="mt-0.5 text-sm text-muted-foreground">{{ role.description }}</p>
        </div>
        <div v-if="!readonly" class="flex shrink-0 items-center gap-1">
          <Button v-if="seeded && role.is_customized" variant="ghost" size="sm" @click="resetDialog?.open()">
            Reset to default
          </Button>
          <Button variant="ghost" size="sm" :disabled="!isDirty" @click="resetDraft">Discard</Button>
          <Button size="sm" :disabled="!isDirty || saving" @click="save">Save</Button>
        </div>
      </div>

      <div class="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Area</TableHead>
              <TableHead v-for="level in LEVELS" :key="level.key" class="w-24 text-center">
                <button
                  v-if="!readonly"
                  type="button"
                  class="whitespace-nowrap hover:text-foreground hover:underline"
                  :title="`Set every area to ${level.label}`"
                  @click="setAll(level.key)"
                >
                  {{ level.label }}
                </button>
                <span v-else class="whitespace-nowrap">{{ level.label }}</span>
              </TableHead>
              <template v-if="hasScopes">
                <TableHead class="w-40">Sees</TableHead>
                <TableHead class="w-40">Edits</TableHead>
              </template>
            </TableRow>
          </TableHeader>
          <TableBody>
            <template v-for="section in sections" :key="section.label">
              <TableRow class="hover:bg-muted/50">
                <TableCell :colspan="LEVELS.length + (hasScopes ? 3 : 1)" class="bg-muted/50 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {{ section.label }}
                </TableCell>
              </TableRow>
              <TableRow v-for="domain in section.domains" :key="domain.key" role="radiogroup" :aria-label="domain.label">
                <TableCell class="py-2">
                  <div class="flex items-center gap-2 font-medium">
                    {{ domain.label }}
                    <Badge v-if="isCustom(domain)" variant="secondary" class="font-normal">Custom</Badge>
                    <Badge v-if="!levelOf(domain, draft[domain.key])" variant="outline" class="font-normal">
                      {{ levelLabel(domain, draft[domain.key]) }}
                    </Badge>
                    <Button
                      v-if="isCustom(domain) && !readonly"
                      variant="ghost"
                      size="sm"
                      class="h-5 w-5 p-0 text-muted-foreground"
                      :title="`Reset ${domain.label} to default`"
                      :aria-label="`Reset ${domain.label} to default`"
                      @click="resetToDefault(domain)"
                    >
                      <RotateCcw class="h-3 w-3" />
                    </Button>
                  </div>
                  <div class="text-xs text-muted-foreground">{{ domain.hint }}</div>
                  <div v-if="differsFromDefault(domain)" class="text-xs text-amber-700">
                    Default: {{ defaultLabel(domain, template[domain.key]) }}
                  </div>
                </TableCell>
                <TableCell v-for="level in LEVELS" :key="level.key" class="py-2 text-center">
                  <button
                    v-if="levelsFor(domain).includes(level.key)"
                    type="button"
                    role="radio"
                    :disabled="readonly"
                    :aria-checked="levelOf(domain, draft[domain.key]) === level.key"
                    :aria-label="`${level.label} for ${domain.label}`"
                    class="inline-flex h-4 w-4 items-center justify-center rounded-full border border-primary align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    @click="setLevel(domain, level.key)"
                  >
                    <span v-if="levelOf(domain, draft[domain.key]) === level.key" class="h-2 w-2 rounded-full bg-primary" />
                  </button>
                  <span v-else class="text-muted-foreground">–</span>
                  <div v-if="level.key === 'full' && domain.fullHint" class="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                    {{ domain.fullHint }}
                  </div>
                </TableCell>
                <TableCell v-for="kind in (hasScopes ? ['sees', 'edits'] as const : [])" :key="kind" class="py-2">
                  <Select
                    v-if="kind === 'sees' ? showsSees(domain) : showsEdits(domain)"
                    :model-value="scopeValue(domain, kind)"
                    :disabled="readonly"
                    @update:model-value="setScope(domain, kind, $event as DataScope)"
                  >
                    <SelectTrigger class="h-8 w-36" :aria-label="`${kind === 'sees' ? 'Sees' : 'Edits'} for ${domain.label}`">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="option in DATA_SCOPES"
                        :key="option"
                        :value="option"
                        :disabled="kind === 'edits' && rank(option) > rank(scopeValue(domain, 'sees'))"
                      >
                        {{ SCOPE_LABELS[option] }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <span v-else class="text-muted-foreground">–</span>
                </TableCell>
              </TableRow>
            </template>
          </TableBody>
        </Table>
      </div>

      <RoleFormDialog v-model:open="renameOpen" mode="rename" :kind="role.kind" :role="role" />
    </template>

    <DialogConfirm
      ref="discardDialog"
      title="Discard unsaved changes?"
      description="Your changes to this role will be lost."
      confirm-text="Discard"
      variant="error"
      @confirm="router.push(listRoute)"
    />
    <DialogConfirm
      ref="resetDialog"
      :title="`Reset ${role?.name ?? 'role'} to default?`"
      description="Every permission goes back to the default for this role."
      confirm-text="Reset"
      variant="error"
      @confirm="resetToDefault()"
    />
  </div>
</template>
